import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  IPaymentPlanData,
  IPaymentPlanScheduleData,
  PaymentPlan,
  PaymentPlanSchedule
} from '@/models';
import {
  ICreatePaymentPlanScheduleInvoiceData,
  IFetchPaymentPlansParams
} from '@/services/payment-plan';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export interface UpdatePaymentPlanStatePayload {
  id: number;
  state: number;
}

export interface IGenPaymentPlanScheduleInvoicePayload {
  scheduleId: number;
  data: ICreatePaymentPlanScheduleInvoiceData;
}

export function useFetchPaymentPlans(filter?: IFetchPaymentPlansParams) {
  const queryKey = ref(['payment-plan', filter]);

  const count = ref<number>(0);

  const { isLoading, isError, error, data: paymentPlans } = useQuery({
    queryKey: queryKey,
    queryFn: () => providerInstance.paymentPlan.fetchPaymentPlans(filter!),
    enabled: computed(() => Boolean(filter)),
    select: data => {
      count.value = data.count;
      return data.results.map(paymentPlan => new PaymentPlan(paymentPlan));
    }
  });

  return { queryKey, paymentPlans, count, error, isError, isLoading };
}

export function useFetchPaymentPlan(id: MaybeRef<number>) {
  const paymentPlanIdRef = ref(id);

  const queryKey = ref(['payment-plan', paymentPlanIdRef]);

  const { data: paymentPlan, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(paymentPlanIdRef.value)),
    queryFn: () =>
      providerInstance.paymentPlan.fetchPaymentPlan(paymentPlanIdRef.value),
    select: data => new PaymentPlan(data)
  });

  return { paymentPlan, isLoading, error, queryKey };
}

export function useCreatePaymentPlan() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-payment-plan'],
    mutationFn: (paymentPlan: IPaymentPlanData) =>
      providerInstance.paymentPlan.createPaymentPlan(paymentPlan),

    onSuccess: data => {
      queryClient.setQueryData(['payment-plan', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['payment-plan']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdatePaymentPlan() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-payment-plan'],
    mutationFn: (paymentPlan: IPaymentPlanData) =>
      providerInstance.paymentPlan.updatePaymentPlan(paymentPlan),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['payment-plan']) });
      //update payment plan schedule data specific to when ex-factory or sign-off date is changed
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan-schedule'])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeletePaymentPlan() {
  const mutationKey = ref(['delete-payment-plan']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      providerInstance.paymentPlan.deletePaymentPlan(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['payment-plan']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useCancelPaymentPlan() {
  const mutationKey = ref(['cancel-payment-plan']);
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      providerInstance.paymentPlan.cancelPaymentPlan(id),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['payment-plan']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

//PaymentPlanSchedule
export function useFetchPaymentPlanSchedules(id: MaybeRef<number>) {
  const paymentPlanIdRef = ref(id);
  const queryKey = ref(['payment-plan', paymentPlanIdRef, 'schedules']);

  const { isLoading, isError, error, data: paymentPlanSchedules } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      providerInstance.paymentPlan.fetchPaymentPlanSchedules(
        paymentPlanIdRef.value
      ),
    enabled: computed(() => Boolean(paymentPlanIdRef.value)),
    select: data => {
      return data.map(
        paymentPlanSchedule => new PaymentPlanSchedule(paymentPlanSchedule)
      );
    }
  });

  return { queryKey, paymentPlanSchedules, error, isError, isLoading };
}

export function useFetchPaymentPlanSchedule(
  paymentPlanId: MaybeRef<number>,
  id: MaybeRef<number>
) {
  const paymentPlanIdRef = ref(paymentPlanId);
  const paymentPlanScheduleIdRef = ref(id);

  const queryKey = ref([
    'payment-plan',
    paymentPlanIdRef,
    'schedules',
    paymentPlanScheduleIdRef
  ]);

  const { data: paymentPlanSchedule, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(paymentPlanScheduleIdRef.value)),
    queryFn: () =>
      providerInstance.paymentPlan.fetchPaymentPlanSchedule(
        paymentPlanIdRef.value,
        paymentPlanScheduleIdRef.value
      ),
    select: data => new PaymentPlanSchedule(data)
  });

  return { paymentPlanSchedule, isLoading, error, queryKey };
}

export function useCreatePaymentPlanSchedule(paymentPlanId: MaybeRef<number>) {
  const paymentPlanIdRef = ref(paymentPlanId);
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-payment-plan-schedule'],
    mutationFn: (paymentPlanSchedule: IPaymentPlanScheduleData) =>
      providerInstance.paymentPlan.createPaymentPlanSchedule(
        paymentPlanIdRef.value,
        paymentPlanSchedule
      ),

    onSuccess: data => {
      queryClient.setQueryData(
        ['payment-plan', paymentPlanIdRef, 'schedules', data.id],
        data
      );
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdatePaymentPlanSchedule(paymentPlanId: MaybeRef<number>) {
  const paymentPlanIdRef = ref(paymentPlanId);
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-payment-plan-schedule'],
    mutationFn: (paymentPlanSchedule: IPaymentPlanScheduleData) =>
      providerInstance.paymentPlan.updatePaymentPlanSchedule(
        paymentPlanIdRef.value,
        paymentPlanSchedule
      ),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef, 'schedules', data.id])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeletePaymentPlanSchedule(paymentPlanId: MaybeRef<number>) {
  const paymentPlanIdRef = ref(paymentPlanId);
  const mutationKey = ref(['delete-payment-plan-schedule']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) =>
      providerInstance.paymentPlan.deletePaymentPlanSchedule(
        paymentPlanIdRef.value,
        id
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef, 'schedules'])
      });
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useCreatePaymentPlanScheduleInvoice(
  paymentPlanId: MaybeRef<number>
) {
  const paymentPlanIdRef = ref(paymentPlanId);
  const mutationKey = ref(['generate-payment-plan-schedule-invoice']);
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (data: IGenPaymentPlanScheduleInvoicePayload) =>
      providerInstance.paymentPlan.createPaymentPlanScheduleInvoice(
        paymentPlanIdRef.value,
        data.scheduleId,
        data.data
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef, 'schedules'])
      });
      // update payment plan to reflect the new invoice
      queryClient.invalidateQueries({
        queryKey: ref(['payment-plan', paymentPlanIdRef])
      });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
